import { useEffect } from 'react';
import { getTokenSubjectId } from 'src/store/auth/decodeToken.js';

import { useImpersonationSessions } from '@hooks/impersonation';
import useStoreAppConfig from '@hooks/useStoreAppConfig';
import useStoreAuth from '@hooks/useStoreAuth';
import { applySentryIdentity, clearSentryIdentity } from '@utils/sentry';

/**
 * Keep the Sentry scope in step with the session.
 *
 * Re-applies the whole identity whenever the user, active hotel chain, role or
 * impersonation state changes, and clears it on logout — a global scope keeps
 * whatever was last written, so a stale hotel chain would silently mislabel every
 * event of the next session.
 *
 * Mounted once, at the top of the provider tree.
 */
export const useSentryContextSync = (): void => {
  const [{ token, userId, role }] = useStoreAuth();
  const [{ hotelChain }] = useStoreAppConfig();
  // Read-only subscriber to the active-session cache, which `Layout` owns and
  // fetches on every authenticated page. `enabled: false` is load-bearing, not an
  // optimization: this hook is mounted at the provider root and never unmounts, so
  // a *fetching* observer here would survive onto the `/logout` route (rendered
  // outside `Layout`) and refetch the session right after `useLogout` clears the
  // cache but before the auth slice is reset — repopulating a `staleTime: Infinity`
  // entry that the next login in the same tab would then inherit, complete with a
  // stale impersonation banner. A disabled observer still receives cache updates,
  // so the tags stay accurate wherever `Layout` is mounted.
  const { session } = useImpersonationSessions({ enabled: false });

  const hotelChainId = hotelChain?.hotelChainId ?? null;
  const impersonationSessionId = session?.id ?? null;

  useEffect(() => {
    if (!token) {
      clearSentryIdentity();
      return;
    }

    // Only meaningful while a session is active, and only trustworthy while it
    // differs from the effective user: an equal value would mean the token no
    // longer identifies the admin, and reporting it would be exactly the
    // "actor inferred from the effective user" this must never do.
    const tokenSubjectId = impersonationSessionId ? getTokenSubjectId(token) : null;
    const realActorUserId = tokenSubjectId === userId ? null : tokenSubjectId;

    applySentryIdentity({
      userId,
      hotelChainId,
      roleId: role,
      isImpersonating: Boolean(impersonationSessionId),
      realActorUserId,
      impersonationSessionId,
    });
  }, [token, userId, role, hotelChainId, impersonationSessionId]);
};

export default useSentryContextSync;
